'use client';

import type { ComputedOrImmediate } from '@funkey/shared';
import { deleteCookie, getCookie, setCookie } from 'cookies-next';
import type { DefaultOptions } from 'cookies-next/lib/types';
import React, { useEffect, useState, useMemo, useSyncExternalStore } from 'react';

/** A higher order function that creates a React context and returns
 * a `useContext(ctx)` wrapper and the context.Provider wrapper containing the
 * return value of the first parameter (useValue)
 *
 * ### Example
 * ```typescriptreact
 * export const [useThemeCtx, ThemeCtxProvider] = contextFactory(() => {
 *     const [theme, setTheme] = useState<'light' | 'dark'>('light')
 *
 *     return {
 *        value: theme,,
 *        setValue: setTheme,
 *        toggleValue: () => setTheme(...),
 *        ...,
 *     } as const
 * })
 *
 *
 * const Page = () => <ThemeCtxProvider>
 *    <Child/>
 * </ThemeCtxProvider>
 *
 * const Child = () => {
 *    const theme = useThemeCtx()
 *
 *    return <p>Current theme: {theme.value}</p>
 * }
 * ```
 */
export const contextFactory = <T extends (props?: any) => any>(
    useValue: T,
    ContextInner?: React.FC<React.PropsWithChildren<ReturnType<T>>>,
): [
    useCtx: () => ReturnType<T>,
    CtxProvider: React.FC<React.PropsWithChildren<T extends (props: infer P) => any ? P : {}>>,
] => {
    // This will never be null because it's directly assigned to the provider and does not escape the scope of this factory
    const ctx = React.createContext<ReturnType<T>>(null as any);

    const useCtx = () => React.useContext(ctx);

    const Inner: React.FC<React.PropsWithChildren> =
        ContextInner === undefined
            ? ({ children }) => children
            : ({ children }) => {
                  const ctxValue = useCtx();
                  return <ContextInner {...ctxValue}>{children}</ContextInner>;
              };

    return [
        useCtx,
        ({ children, ...props }) => (
            <ctx.Provider value={useValue(props)}>
                <Inner>{children}</Inner>
            </ctx.Provider>
        ),
    ];
};

export const useToggleState = (initialValue = false) => {
    const [state, setState] = React.useState(initialValue);

    return useMemo(
        () => ({
            state,
            setState,
            toggle(value?: boolean) {
                setState(prev => value ?? !prev);
            },
            setTrue() {
                setState(true);
            },
            setFalse() {
                setState(false);
            },
        }),
        [state, setState],
    );
};

export type StateObject<T> = Omit<ReturnType<typeof useStateObject<T>>, 'handleChange'>;
/** Works just like `useState()` but returns it as an object instead of an array that needs to be destructured.
 *
 * This just returns the CRUD of `useState()` in an object, which is quicker to find all references on and pass to other components. */
export function useStateObject<T>(initialState: T | (() => T)) {
    const [value, set] = useState(initialState);
    const handleChange = (key: keyof T, newValue: T[keyof T]) =>
        set(prev => ({ ...prev, [key]: newValue }));

    return useMemo(() => ({ value, set, handleChange }), [value]);
}

/** Converts a classic react `[state, setState]` tuple to an object.
 *
 * This is useful if you want to easilly pas around states and their CRUD */
export function toStateObject<V, S extends (...args: any[]) => any>(
    [value, setValue]: [value: V, setValue: S]
) {
    return { value, setValue } as const;
} // prettier-ignore

/** A state hook to work with record state to modify specific keys.
 *
 * NOTE: this only works on the first level of the object. Deeply nested objects are not supported. */
export function useRecordState<T extends Record<string, unknown>>(initialState: T | (() => T)) {
    const [value, setValue] = useState(initialState);

    return useMemo(() => {
        const sdk = {
            value,
            setValue,
            /** Sets the value of a specific key in the record. */
            setKey<K extends keyof T>(key: K, value: T[K] | ((currentValue: T[K]) => T[K])) {
                return setValue(prev => ({
                    ...prev,
                    [key]:
                        typeof value === 'function'
                            ? (value as (v: T[K]) => T[K])(prev[key])
                            : value,
                }));
            },
            /** Gets a subset of the record state by the given `key`.
             *
             * This is useful if you want to pass a subset of the record state to a component. */
            getKeyState<K extends keyof T>(key: K) {
                return {
                    value: value[key],
                    setValue(value: T[K] | ((currentValue: T[K]) => T[K])) {
                        return sdk.setKey(key, value);
                    },
                } as const;
            },
        } as const;

        return sdk;
    }, [value]);
}

/** A react state array that acts like a set
 * NOTE: the returned object is **NOT** momoised. Use `.value` in dependency arrays  */
export function useStateSet<T extends string | number>(initialState: T[]) {
    const [state, setState] = useState(new Set(initialState));

    return useMemo(
        () =>
            ({
                value: Array.from(state.values()),
                setValue: (value: React.SetStateAction<T[]>) => {
                    setState(
                        prev =>
                            new Set(
                                typeof value === 'function' ? value([...prev.values()]) : value,
                            ),
                    );
                },
                has(value: T) {
                    return state.has(value);
                },
                add(value: T) {
                    setState(prev => new Set(prev).add(value));
                },
                remove(value: T) {
                    setState(prev => {
                        const copy = new Set(prev);
                        copy.delete(value);
                        return copy;
                    });
                },
                toggle(value: T) {
                    setState(prev => {
                        const copy = new Set(prev);
                        if (copy.has(value)) copy.delete(value);
                        else copy.add(value);

                        return copy;
                    });
                },
            }) as const,
        [state],
    );
}

export function useArray<T>(initialState: T[] | (() => T[])) {
    const [state, setState] = useState(initialState);

    type IdxState = {
        value: T;
        setValue: React.Dispatch<React.SetStateAction<T>>;
    };

    const sdk = useMemo(
        () =>
            ({
                value: state,
                setValue: setState,
                push(...values: T[]): void {
                    setState(prev => [...prev, ...values]);
                },
                removeAt(idx: number): void {
                    setState(prev => prev.filter((_, i) => i !== idx));
                },
                updateAt(idx: number, value: React.SetStateAction<T>): void {
                    setState(prev => {
                        const copy = [...prev];
                        if (idx < 0 || idx >= copy.length) throw new Error('Segfault');

                        copy[idx] =
                            typeof value === 'function'
                                ? (value as (prev: T) => T)(copy[idx]!)
                                : value;

                        return copy;
                    });
                },
                getIdxState(idx: number): null | IdxState {
                    if (idx < 0 || idx >= state.length) return null;

                    return {
                        value: state[idx]!,
                        setValue: (value: React.SetStateAction<T>) => sdk.updateAt(idx, value),
                    };
                },
                mapState<U>(callback: (idxState: IdxState, idx: number, array: T[]) => U): U[] {
                    return state.map((_, idx, array) =>
                        callback(sdk.getIdxState(idx)!, idx, array),
                    );
                },
            }) as const,
        [state, setState],
    );

    return sdk;
}

export function useAsyncFn<T extends (...args: any[]) => Promise<any>>(asyncFn: T) {
    const [isPending, setIsPending] = useState(false);

    async function execute(...args: Parameters<T>) {
        if (isPending) {
            throw new Error(
                'Async function can not be called again when the previous call is still pending',
            );
        }

        const promise = asyncFn(...args);
        if (!(promise instanceof Promise)) throw new Error('Callback does not return a promise');

        setIsPending(true);
        return promise.finally(() => {
            setIsPending(false);
        });
    }

    return Object.assign(execute, {
        isPending,
    });
}

/** WARN: this hook currently doesn't support the cookie `name` changing */
export function useCookie(
    name: string,
    options: Pick<DefaultOptions, 'path' | 'maxAge'> & {
        /** From `cookies().get(name)` in a Server Component so SSR/hydration match `document.cookie` */
        initialFromServer?: string | null;
    } = {},
) {
    const { initialFromServer, path, maxAge } = options;
    const [state, setState] = useState<string | null>(() => {
        if (initialFromServer !== undefined) {
            return initialFromServer;
        }
        return getCookie(name) || null;
    });
    const cookieOpts = { path, maxAge };

    return useMemo(
        () => ({
            state,
            setState(value: string | null) {
                if (!value) {
                    deleteCookie(name, cookieOpts);
                } else {
                    setCookie(name, value, cookieOpts);
                }

                setState(value);
            },
        }),
        [state, name, path, maxAge],
    );
}

export function useAsyncEffect(
    callback: () => Promise<void | (() => void)>,
    dependencies: unknown[] = [],
) {
    useEffect(() => {
        const promise = callback();

        return () => {
            promise.then(res => {
                if (typeof res === 'function') res();
            });
        };
    }, dependencies);
}

export function useBoolean(initialValue: ComputedOrImmediate<() => boolean>) {
    const [state, setState] = useState(initialValue);

    return useMemo(
        () => ({
            state,
            setState,
            setTrue() {
                return setState(true);
            },
            setFalse() {
                return setState(false);
            },
        }),
        [state, setState],
    );
}

/** Utility function to parse the new state value from a `React.SetStateAction<T>`
 * @returns The new state */
export function executeSetStateAction<T>(action: React.SetStateAction<T>, previousState: T): T {
    return typeof action === 'function' ? (action as (prev: T) => T)(previousState) : action;
}

/** Sync with an external system once, on mount. The only sanctioned wrapper
 * around a bare `useEffect` for components (see the no-use-effect rule). */
export function useMountEffect(effect: () => void | (() => void)) {
    useEffect(effect, []);
}

/** False on the server and during hydration, true on the client after that. Use it to keep the
 * first client render identical to the server's when the content depends on the browser. */
export function useHasMounted(): boolean {
    return useSyncExternalStore(
        subscribeToNothing,
        () => true,
        () => false,
    );
}

function subscribeToNothing() {
    return () => {};
}
